import { Box, Button, Divider, Typography, type BoxProps } from "@mui/joy";
import { useTranslation } from "react-i18next";
import iosDownloadIcon from "../assets/Download_on_the_App_Store_Badge_purple_bg.svg";
import androidDownloadIcon from "../assets/Download_on_the_Google_Play_Store_Badge_purple_bg.svg";

// Add interface for BeforeInstallPromptEvent
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

type AppStoreButtonsProps = {
  badgeHeight?: number;
  sx?: BoxProps["sx"];
  deferredPrompt?: BeforeInstallPromptEvent | null;
  onPromptUsed?: () => void;
};

export function AppStoreButtons({
  badgeHeight = 28,
  sx,
  deferredPrompt,
  onPromptUsed,
}: AppStoreButtonsProps): JSX.Element {
  const { t } = useTranslation();

  const handleAddToHomeScreen = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      console.log(`User response to install prompt: ${outcome}`);
      onPromptUsed?.();
    } else {
      // For iOS or when prompt is not available
      alert(t('auth.addToHomeScreenInstructions') || 'To add this app to your home screen:\n\niOS: Tap the Share button, then tap "Add to Home Screen"\n\nAndroid: Tap the menu button (⋮) and select "Install app" or "Add to Home screen"');
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        alignItems: "center",
        justifyContent: "center",
        ...sx,
      }}
    >
      <Divider sx={{ width: "100%", my: 1 }} />
      <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
        {t("appStore.downloadMessage")}
      </Typography>
      <Box
        sx={{
          display: "flex",
          gap: 1,
          alignItems: "center",
          justifyContent: "center",
        }}
      >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minWidth: "auto",
        }}
        component="a"
        href="https://apps.apple.com/us/app/povchat-ai-characters-to-chat/id6744286631"
        target="_blank"
        rel="nofollow"
      >
        <img
          src={iosDownloadIcon}
          alt="Download on the App Store"
          loading="lazy"
          style={{
            height: `${badgeHeight}px`,
            display: "block",
            textAlign: "center",
          }}
        />
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minWidth: "auto",
        }}
        component="a"
        href="https://play.google.com/store/apps/details?id=ai.povchat.characters"
        target="_blank"
        rel="nofollow"
      >
        <img
          src={androidDownloadIcon}
          alt="Download on the Google Play Store"
          loading="lazy"
          style={{
            height: `${badgeHeight}px`,
            display: "block",
            textAlign: "center",
          }}
        />
      </Box>
      </Box>
      <Button
        variant="outlined"
        color="neutral"
        size="sm"
        onClick={handleAddToHomeScreen}
        sx={{ width: "100%" }}
      >
        {t('auth.addToHomeScreen') || '📱 Add to Home Screen'}
      </Button>
    </Box>
  );
}
